import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { Pwa } from '@freelancer/pwa';
import type { GalleryItem } from '@freelancer/ui/gallery-types';
import { GalleryItemType } from '@freelancer/ui/gallery-types';
import { DEFAULT_DPR1_SCALING_FACTOR } from '@freelancer/ui/picture';
import { UserAgent } from '@freelancer/user-agent';
import { isDefined } from '@freelancer/utils';
import type { GalleryItem as LightGalleryItem } from 'lightgallery/lg-utils';
import { Assets } from '../assets';
import { MediaViewerItemCaptionComponent } from './media-viewer-item-caption.component';
import { MediaViewerItemContentComponent } from './media-viewer-item-content.component';
import { MediaViewerItemComponent } from './media-viewer-item.component';
import { getFileExtension, resolveAssetUrl } from '../helpers/helpers';
import { MediaViewerLightGalleryComponent } from './media-viewer-light-gallery/media-viewer-light-gallery.component';
import { createVideoSource } from './media-viewer-light-gallery/media-viewer-light-gallery.helpers';
import { TESTING_MEDIA_VIEWER_DISABLED } from './media-viewer.config';
import type { MediaItem } from './media-viewer.types';
import {
  DEFAULT_FILE_THUMBNAIL,
  DEFAULT_FILE_THUMBNAIL_FILE_TYPE,
  DEFAULT_FILE_THUMBNAIL_HEIGHT,
  DEFAULT_FILE_THUMBNAIL_WIDTH,
  DEFAULT_VIDEO_FILE_THUMBNAIL,
  MediaViewerMode,
} from './media-viewer.types';

@Component({
  standalone: true,
  selector: 'fl-media-viewer',
  imports: [CommonModule, MediaViewerLightGalleryComponent],
  template: `
    @if (!testingMediaViewerDisabled) {
      <fl-media-viewer-light-gallery
        #lightGalleryComponent
        [mode]="mode"
        [ariaDescribedBy]="ariaDescribedBy"
        [ariaLabelledBy]="ariaLabelledBy"
        [enableFullscreen]="enableFullscreen"
        [enablePhotoRotations]="photoRotations"
        [lightGalleryItems]="lightGalleryItems"
        [shouldLoopBack]="shouldLoopBack"
        [showCloseButton]="closeable"
        [showCounter]="showCounter"
        [showDownloadButton]="downloadable"
        [slideTransition]="'slide'"
        (onAfterClose)="onAfterClose.emit()"
      >
        <ng-content></ng-content>
      </fl-media-viewer-light-gallery>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerComponent implements OnChanges, AfterViewInit {
  private assets = inject(Assets);
  private pwa = inject(Pwa);
  private userAgent = inject(UserAgent);
  readonly testingMediaViewerDisabled = inject(TESTING_MEDIA_VIEWER_DISABLED, {
    optional: true,
  });
  private platformId = inject(PLATFORM_ID);

  @Input()
  @HostBinding('attr.data-mode')
  mode: MediaViewerMode = 'inline';

  /**
   * Items to be displayed when the gallery is in modal mode.
   */
  @Input() galleryItems?: readonly MediaItem[] | readonly GalleryItem[];
  /**
   * Whether the gallery should loop back to the first item after the last item.
   */
  @Input() shouldLoopBack = true;
  @Input() closeable = true;
  @Input() showCounter = true;
  @Input() downloadable = true;
  /**
   * Whether to allow the user to rotate the photo in the media.
   */
  @Input() photoRotations = false;
  @Input() enableFullscreen = false;
  @Input() ariaDescribedBy?: string;
  @Input() ariaLabelledBy?: string;

  @Output() loaded = new EventEmitter<void>();

  @Output() onAfterClose = new EventEmitter<void>();

  @ViewChild('lightGalleryComponent')
  lightGalleryComponent: MediaViewerLightGalleryComponent;
  lightGalleryItems?: readonly LightGalleryItem[];

  readonly defaultFileThumbnail = resolveAssetUrl(
    this.assets,
    DEFAULT_FILE_THUMBNAIL,
  );
  readonly defaultVideoThumbnail = resolveAssetUrl(
    this.assets,
    DEFAULT_VIDEO_FILE_THUMBNAIL,
  );

  readonly TEXT_NOT_SUPPORTED_FILE = $localize`This file cannot be previewed.`;
  readonly TEXT_NOT_SUPPORTED_DOWNLOAD = $localize`Download`;
  readonly TEXT_NOT_SUPPORT_DOWNLOAD_ARIA = $localize`Download button`;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'galleryItems' in changes &&
      changes.galleryItems.currentValue &&
      this.mode === 'modal'
    ) {
      this.lightGalleryItems = changes.galleryItems.currentValue.map(
        (item: MediaItem): LightGalleryItem => {
          const {
            alt,
            downloadUrl,
            fileName,
            height,
            source,
            thumbnailSource,
            type,
            width,
          } = item;

          const src = resolveAssetUrl(this.assets, source);

          const mediaItem: Partial<LightGalleryItem> = {
            downloadUrl: downloadUrl ?? src,
            alt: alt ? this.escapeHtmlEntities(alt) : undefined,
            fileName: fileName ? this.escapeHtmlEntities(fileName) : undefined,
            height,
            width: isDefined(width) ? width.toString() : undefined,
            subHtml: this.createCaption(item),
          };

          if (type === GalleryItemType.PICTURE) {
            const { srcset, sizes, fileType, thumbnailOptimizationParameters } =
              item;
            let thumb = thumbnailSource
              ? resolveAssetUrl(this.assets, thumbnailSource)
              : src;

            if (thumbnailOptimizationParameters) {
              const scalingFactor = this.getScalingFactor();
              const {
                width: thumbWidth,
                height: thumbHeight,
                cropToFit,
              } = thumbnailOptimizationParameters;
              const thumbUrl = new URL(thumb);
              if (thumbWidth) {
                thumbUrl.searchParams.set(
                  'width',
                  Math.floor(thumbWidth * scalingFactor).toString(),
                );
              }
              if (thumbHeight) {
                thumbUrl.searchParams.set(
                  'height',
                  Math.floor(thumbHeight * scalingFactor).toString(),
                );
              }
              // The `fit` parameter is only valid if both width and height are specified.
              // When either height or width is not provided but fit is specified,
              // image optimization will be skipped by Fastly.
              if (cropToFit && thumbWidth && thumbHeight) {
                thumbUrl.searchParams.set('fit', 'crop');
              }

              thumbUrl.searchParams.set('image-optimizer', 'force');

              thumb = thumbUrl.toString();
            }

            return {
              ...mediaItem,
              src,
              thumb,
              srcset,
              sizes,
              type: fileType
                ? `image/${fileType}`
                : `image/${getFileExtension(fileName)}`,
            };
          }

          if (type === GalleryItemType.VIDEO) {
            const { fileType, posterSrc } = item;
            const poster = posterSrc
              ? resolveAssetUrl(this.assets, posterSrc)
              : undefined;
            const thumb = thumbnailSource
              ? resolveAssetUrl(this.assets, thumbnailSource)
              : this.defaultVideoThumbnail;
            const videoWidth = thumbnailSource
              ? mediaItem.width
              : DEFAULT_FILE_THUMBNAIL_WIDTH;
            const videoHeight = thumbnailSource
              ? mediaItem.height
              : DEFAULT_FILE_THUMBNAIL_HEIGHT;

            return {
              ...mediaItem,
              thumb,
              poster,
              width: videoWidth,
              height: videoHeight,
              video: createVideoSource(src, fileType),
            };
          }

          // Only show PDF in modal mode if the PWA is not Safari on web.
          if (
            type === GalleryItemType.PDF &&
            isPlatformBrowser(this.platformId) &&
            this.pwa.getPlatform() === 'web' &&
            this.userAgent.getUserAgent().getBrowser().name !== 'Safari' &&
            !this.userAgent.isMobileDevice()
          ) {
            const pdfUrl = new URL(src);
            if (!pdfUrl.toString().startsWith('data:application/pdf')) {
              pdfUrl.searchParams.set('disposition', 'inline');
            }

            return {
              ...mediaItem,
              thumb: this.defaultFileThumbnail,
              src: pdfUrl.toString(),
              type: 'application/pdf',
              width: DEFAULT_FILE_THUMBNAIL_WIDTH,
              height: DEFAULT_FILE_THUMBNAIL_HEIGHT,
              iframe: true,
            };
          }

          // Type is GalleryItemType.FILE
          return {
            ...mediaItem,
            src: this.defaultFileThumbnail,
            thumb: thumbnailSource
              ? resolveAssetUrl(this.assets, thumbnailSource)
              : this.defaultFileThumbnail,
            type: DEFAULT_FILE_THUMBNAIL_FILE_TYPE,
            width: DEFAULT_FILE_THUMBNAIL_WIDTH,
            height: DEFAULT_FILE_THUMBNAIL_HEIGHT,
          };
        },
      );
    }
  }

  ngAfterViewInit(): void {
    this.loaded.emit();
  }

  goToItem(index: number): void {
    this.lightGalleryComponent.goToItem(index);
  }

  openModal(index?: number): void {
    this.lightGalleryComponent.openModal(index);
  }

  closeModal(): void {
    this.lightGalleryComponent.closeModal();
  }

  isModalOpen(): boolean {
    return this.lightGalleryComponent.isModalOpen();
  }

  private createCaption(item: MediaItem): string {
    const fileNameCaption = `
      <p><b>${this.escapeHtmlEntities(item.fileName)}</b></p>
    `;

    if (item.caption) {
      return `
        ${fileNameCaption}
        <p>${this.escapeHtmlEntities(item.caption)}</p>
      `;
    }

    if (
      [
        GalleryItemType.PICTURE,
        GalleryItemType.VIDEO,
        GalleryItemType.PDF,
      ].includes(item.type)
    ) {
      return fileNameCaption;
    }

    return `
      ${fileNameCaption}
      <p>${this.TEXT_NOT_SUPPORTED_FILE}</p>
    `;
  }

  /**
   * Manually escape HTML entities to prevent XSS attacks.
   *
   * Angular's DomSanitizer SecurityContext.HTML is designed for HTML content, not HTML attributes.
   * When user input containing quotes and HTML-like syntax is inserted into HTML attributes
   * via string concatenation (as LightGallery does), it can break out of the attribute
   * and inject malicious code.
   *
   * In addition , this method properly escapes all characters that could break
   * HTML structure, which is not the case for sanitize(), since it is designed
   * to preserve safe HTML markup
   */
  private escapeHtmlEntities(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private getScalingFactor(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return DEFAULT_DPR1_SCALING_FACTOR;
    }

    // eslint-disable-next-line no-restricted-globals, no-restricted-properties
    const { devicePixelRatio } = window;
    return devicePixelRatio > 1
      ? devicePixelRatio
      : DEFAULT_DPR1_SCALING_FACTOR;
  }
}

export const MEDIA_VIEWER_DIRECTIVES = [
  MediaViewerItemCaptionComponent,
  MediaViewerItemContentComponent,
  MediaViewerItemComponent,
  MediaViewerComponent,
] as const;
